import { useInfiniteQueryBuilder, useQueryBuilder } from "../../useBuilders";
import {
  ASSET_ANALYTICS,
  DETAIL,
  FIRST_CAPTURED,
  JOURNEY,
  LATEST_CAMPAIGN,
  LEADS,
  PAGE_ANALYTICS,
  SUMMARY,
} from "../../queryKeys";
import { leadsApi } from "src/services/experiences/leadsAndReports/leads";
import { ReportRequest } from "src/types/experiences/leadsAndReports";

const FIVE_MIN = 5 * 60 * 1000;

// L1
export const useLeadSummaryQuery = (storyboardId: string) =>
  useQueryBuilder(
    [LEADS, SUMMARY, storyboardId],
    () => leadsApi.fetchSummary(storyboardId),
    false,
    FIVE_MIN,
    false,
    undefined,
    Boolean(storyboardId),
    1,
  );

// L2
export const useLeadDetailQuery = (profileId: string, request: ReportRequest, key: string) =>
  useQueryBuilder(
    [LEADS, DETAIL, profileId, key],
    () => leadsApi.fetchLeadDetail(profileId, request),
    false,
    FIVE_MIN,
    "always",
    undefined,
    Boolean(profileId),
    1,
  );

// L3
export const useFirstCapturedLeadQuery = (profileId: string, request: ReportRequest, key: string) =>
  useQueryBuilder(
    [LEADS, FIRST_CAPTURED, profileId, key],
    () => leadsApi.fetchFirstCaptured(profileId, request),
    false,
    FIVE_MIN,
    false,
    undefined,
    Boolean(profileId),
    1,
  );

// L4
export const useLatestCampaignLeadQuery = (profileId: string, request: ReportRequest, key: string) =>
  useQueryBuilder(
    [LEADS, LATEST_CAMPAIGN, profileId, key],
    () => leadsApi.fetchLatestCampaign(profileId, request),
    false,
    FIVE_MIN,
    false,
    undefined,
    Boolean(profileId),
    1,
  );

// L5 — infinite (lead journey)
export const useLeadJourneyInfiniteQuery = (
  profileId: string,
  request: ReportRequest,
  key: string,
  limit = 20,
  enabled = true,
) =>
  useInfiniteQueryBuilder(
    [LEADS, JOURNEY, profileId, key],
    ({ pageParam }) => leadsApi.fetchJourney(profileId, request, pageParam, limit),
    0,
    (lastPage) => {
      const records = lastPage?.records ?? [];
      if (records.length < limit) return undefined;
      const current = lastPage?.currentPage ?? 0;
      return current + 1;
    },
    undefined,
    enabled && Boolean(profileId),
    false,
    1,
  );

// L6/L8 — page interactions
export const useLeadPageInteractionsQuery = (
  profileId: string,
  request: ReportRequest,
  variant: "interactive" | "landing",
  key: string,
  page: number,
  enabled = true,
) =>
  useQueryBuilder(
    [LEADS, PAGE_ANALYTICS, variant, profileId, key, String(page)],
    () => leadsApi.fetchPageInteractions(profileId, request, variant, page, 5),
    false,
    FIVE_MIN,
    false,
    undefined,
    enabled && Boolean(profileId),
    1,
  );

export const useLeadPageInteractionsChartQuery = (
  profileId: string,
  request: ReportRequest,
  variant: "interactive" | "landing",
  key: string,
  enabled = true,
) =>
  useQueryBuilder(
    [LEADS, PAGE_ANALYTICS, "TREND", variant, profileId, key],
    () => leadsApi.fetchPageInteractions(profileId, request, variant, 0, 6),
    false,
    FIVE_MIN,
    false,
    undefined,
    enabled && Boolean(profileId),
    1,
  );

// L10 — asset interactions
export const useLeadAssetInteractionsQuery = (
  profileId: string,
  request: ReportRequest,
  key: string,
  page: number,
  enabled = true,
) =>
  useQueryBuilder(
    [LEADS, ASSET_ANALYTICS, profileId, key, String(page)],
    () => leadsApi.fetchAssetInteractions(profileId, request, page, 4),
    false,
    FIVE_MIN,
    false,
    undefined,
    enabled && Boolean(profileId),
    1,
  );

export const useLeadAssetInteractionsChartQuery = (
  profileId: string,
  request: ReportRequest,
  key: string,
  enabled = true,
) =>
  useQueryBuilder(
    [LEADS, ASSET_ANALYTICS, "TREND", profileId, key],
    () => leadsApi.fetchAssetInteractions(profileId, request, 0, 6),
    false,
    FIVE_MIN,
    false,
    undefined,
    enabled && Boolean(profileId),
    1,
  );
